import {
  CloseOutlined,
  LinkOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  GET_CHANNEL_IDEA,
  Idea,
  IDEA_UPLOAD_DOCUMENTS,
  UploadedFile,
} from '@frontend/data-access';
import {
  FileUpload,
  handleOpenDocument,
  supportedDocumentFormats,
} from '@frontend/shared/ui';
import type { UploadFile } from 'antd';
import { App, Button, Divider, Form, Input, Modal, Popconfirm } from 'antd';
import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

function IdeaDetailAdditionalAttachments({ idea }: { idea: Idea }) {
  const { t } = useTranslation('common');
  const { notification } = App.useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [isIncomplete, setIsIncomplete] = useState({
    video: false,
    document: false,
  });

  const [updateIdeaDocuments, { loading }] = useMutation(
    IDEA_UPLOAD_DOCUMENTS,
    {
      refetchQueries: [
        {
          query: GET_CHANNEL_IDEA,
          variables: { ideaId: idea.id },
        },
      ],
      onCompleted: () => {
        setIsModalOpen(false);
      },
    }
  );

  const getDomainFromUrl = (url: string) => {
    try {
      // Add http:// prefix if missing
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;

      // Get hostname from URL
      const domain = new URL(formattedUrl).hostname;

      // Remove www. prefix if present
      const cleanDomain = domain.startsWith('www.') ? domain.slice(4) : domain;

      // Get domain parts
      const [subdomain, tld] = cleanDomain.split('.');

      // Truncate long subdomains
      const truncatedSubdomain =
        subdomain.length > 20 ? `${subdomain.slice(0, 20)}...` : subdomain;

      return `${truncatedSubdomain}.${tld}`;
    } catch (error) {
      console.error(error);
      return t('invalid_url');
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleModalSave = () => {
    form.submit();
  };

  const handleRemoveVideo = (video: string) => {
    const updatedVideos = idea.reviewerVideos?.filter((v) => v !== video) || [];
    updateIdeaDocuments({
      variables: {
        ideaId: idea.id,
        documents: idea.reviewerDocuments,
        videos: updatedVideos,
      },
    });
  };

  const handleRemoveDocument = (document: UploadedFile) => {
    const updatedDocuments =
      idea.reviewerDocuments?.filter((d) => d.id !== document.id) || [];
    updateIdeaDocuments({
      variables: {
        ideaId: idea.id,
        documents: updatedDocuments,
        videos: idea.reviewerVideos,
      },
    });
  };

  const handleFinish = (values: {
    document?: UploadedFile;
    video?: string;
  }) => {
    const documents = values.document
      ? [...(idea.reviewerDocuments || []), values.document]
      : idea.reviewerDocuments;
    const videos = values.video
      ? [...(idea.reviewerVideos || []), values.video]
      : idea.reviewerVideos;
    updateIdeaDocuments({
      variables: {
        ideaId: idea.id,
        documents,
        videos,
      },
    });
  };

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-gray-700 text-sm font-normal">
            {t('additional_attachments')}
          </div>
          <Button
            type="link"
            size="small"
            onClick={handleModalToggle}
            data-test="add-attachments-btn"
          >
            {t('upload')}
          </Button>
        </div>
        {idea.reviewerVideos?.length || idea.reviewerDocuments?.length ? (
          <div>
            <div className="flex flex-wrap gap-2">
              {idea.reviewerVideos &&
                idea.reviewerVideos.map((video, index) => (
                  <div
                    key={index}
                    className="px-3 py-1 bg-gray-300 rounded-full flex items-center gap-2 cursor-pointer truncate whitespace-nowrap overflow-hidden"
                  >
                    <span className="text-gray-600 flex items-center">
                      <LinkOutlined />
                    </span>
                    <a
                      href={
                        video.startsWith('http://') ||
                        video.startsWith('https://')
                          ? video
                          : `https://${video}`
                      }
                      target="_blank"
                      key={video}
                      rel="noreferrer"
                    >
                      <span className="cursor-pointer">
                        {getDomainFromUrl(video)}
                      </span>
                    </a>
                    <Popconfirm
                      title={t('delete_link')}
                      onConfirm={() => handleRemoveVideo(video)}
                      okText={t('yes')}
                      cancelText={t('no')}
                    >
                      <Button type="text" size="small">
                        <CloseOutlined />
                      </Button>
                    </Popconfirm>
                  </div>
                ))}
              {idea.reviewerDocuments &&
                idea.reviewerDocuments.map((file) => (
                  <div
                    key={file.id}
                    className="px-3 py-1 bg-gray-300 rounded-full flex items-center gap-2 cursor-pointer truncate whitespace-nowrap overflow-hidden"
                  >
                    <PaperClipOutlined />
                    <span
                      className="cursor-pointer hover:text-primary-600"
                      onClick={() => handleOpenDocument(file, notification, t)}
                    >
                      {`${file.metadata?.filename
                        ?.split('.')[0]
                        .slice(0, 20)}.${
                        file.metadata?.filename?.split('.')[1]
                      }`}
                    </span>
                    <Popconfirm
                      title={t('delete_file')}
                      onConfirm={() => handleRemoveDocument(file)}
                      okText={t('yes')}
                      cancelText={t('no')}
                    >
                      <Button type="text" size="small">
                        <CloseOutlined />
                      </Button>
                    </Popconfirm>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-sm">{t('no_attachments')}</div>
        )}
      </div>

      <Modal
        title={t('file_upload')}
        open={isModalOpen}
        onCancel={handleModalToggle}
        footer={[
          <Button key="close" onClick={handleModalToggle} shape="round">
            {t('close')}
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleModalSave}
            shape="round"
            loading={loading}
            data-test="attachment-save-btn"
          >
            {t('save')}
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (!values.document && !values.video) {
              form.setFields([
                {
                  name: 'video',
                  errors: [t('enter_link_required')],
                },
              ]);
            } else {
              handleFinish(values);
            }
          }}
        >
          <div className="space-y-6">
            <Form.Item name="video" label={t('enter_link')}>
              <Input
                placeholder={t('paste_link')}
                data-test="video-link"
                maxLength={250}
              />
            </Form.Item>

            <Divider>{t('or')}</Divider>

            <Form.Item name="document" data-test="document">
              <FileUpload
                description={
                  <div className="mb-4">{t('upload_pitch_document')}</div>
                }
                onUploadSuccess={(file: UploadedFile) => {
                  form.setFieldValue('document', file);
                }}
                onRemove={(file: UploadFile) => {
                  form.setFieldValue('document', []);
                }}
                compact={false}
                maxFileSize={10485760}
                supportedFileTypes={supportedDocumentFormats}
                handleUploadLoading={(loading) =>
                  setIsIncomplete({ ...isIncomplete, document: loading })
                }
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default IdeaDetailAdditionalAttachments;
